import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollReveal from '../hooks/useScrollReveal';

const projects = [
  {
    title: 'PTOS-M Mobile',
    subtitle: 'Pelindo Terminal Operating System — Multipurpose',
    description:
      'Mobile application for managing port operations. Real-time data, streamlined logistics, and improved operational workflows.',
    technologies: ['Android Native', 'Java'],
    imageUrl:
      'https://s3.pelindo.co.id/genc/ptosm/QMD8YHMN0GATlKmFuDCaUQDP9H30B3M4Od19vFzr.jpeg',
  },
  {
    title: 'PTOS-M Web',
    subtitle: 'Pelindo Terminal Operating System — Multipurpose',
    description:
      'Web application managing port operations. Enhances efficiency with real-time data and streamlined logistics.',
    technologies: ['ASP.NET MVC', 'Spring Boot', 'Vue.js', 'Oracle SQL', 'Stored Procedure'],
    imageUrl:
      'https://s3.pelindo.co.id/genc/ptosm/H5wjmwFd5E7zhENhMI16jlf4GDaSq1kDHpFgRMrk.png',
  },
  {
    title: 'IBS',
    subtitle: 'Integrated Billing System',
    description:
      'Customer-facing billing platform. Managed improvements, new features, and technical issue resolution.',
    technologies: ['ASP.NET MVC', 'Spring Boot', 'Vue.js', 'Oracle SQL', 'Stored Procedure'],
    imageUrl:
      'https://s3.pelindo.co.id/genc/ptosm/SbmfzbohMzNzBqEuGtGPzBtUoBcDCMajOp1eDXet.png',
  },
];

const ImageLightbox = ({ src, onClose }) => (
  <motion.div
    className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.2 }}
    onClick={onClose}
  >
    <motion.img
      src={src}
      alt="Project preview"
      className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.85, opacity: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      onClick={(e) => e.stopPropagation()}
    />
    <button
      onClick={onClose}
      className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl leading-none transition-colors"
      aria-label="Close preview"
    >
      &times;
    </button>
  </motion.div>
);

const Projects = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const { ref, inView } = useScrollReveal({ threshold: 0.1 });

  return (
    <section id="projects" className="section-padding bg-[var(--bg-secondary)]">
      <div className="container-tight">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <h2 className="section-heading accent-underline inline-block mb-12">
            Projects
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project, idx) => (
              <motion.article
                key={project.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: idx * 0.1, ease: 'easeOut' }}
                className="bg-[var(--bg-primary)] border border-[var(--border)] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 group"
              >
                {/* Image */}
                <div
                  className="relative h-48 overflow-hidden cursor-pointer"
                  onClick={() => setSelectedImage(project.imageUrl)}
                >
                  <img
                    src={project.imageUrl}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                    <span className="text-white text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/50 px-3 py-1.5 rounded-full">
                      Click to preview
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-[var(--text-primary)] tracking-tight">
                    {project.title}
                  </h3>
                  <p className="text-xs text-accent font-medium mt-0.5 mb-2">
                    {project.subtitle}
                  </p>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">
                    {project.description}
                  </p>

                  {/* Tech tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2.5 py-1 text-[11px] font-medium font-mono bg-[var(--bg-tertiary)] text-[var(--text-secondary)] rounded-md"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <ImageLightbox
            src={selectedImage}
            onClose={() => setSelectedImage(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
